import {useEffect, useRef} from "react"

import {
  buildCaseInfo,
  toCaseInfoInput,
} from "@frontend/utils/appHostFunctions"
import {setHostCaseInfo} from "@frontend/utils/hostCaseInfo"
import {
  emitHostDiagnosisCompleted,
  emitHostDiagnosisInconclusive,
  emitHostRecommendationPreviewed,
  intakeFromProductInfo,
  remediationTypeToHostJourneyStep,
  syncHostIntakeFromProductInfo,
  usesJourneyHostEvents,
} from "@frontend/utils/hostJourneyEvents"
import type {CaseEvent} from "@ri2/db/client"
import type {Recommendation} from "@ri2/db/client"
import {resolveAgeMonths} from "@ri2/db/outcomePolicy/ageMonths"
import {
  OPTIMISTIC_AI_MESSAGE_ID,
} from "@frontend/routes/cases/[caseId]/util/useSendMessage"

type ProductInfoSource = Parameters<typeof intakeFromProductInfo>[0]

type AiMessageEvent = Extract<CaseEvent, {eventType: "message"}> & {type: "ai"}

const isTrackableAiMessage = (event: CaseEvent): event is AiMessageEvent =>
  event.eventType === "message" &&
  event.type === "ai" &&
  event.id !== OPTIMISTIC_AI_MESSAGE_ID

type Workflow = "diagnosis" | "ecommerce" | "customer_service" | "unsupported"

const isUnsupportedMode = (mode: string | null): boolean =>
  mode === "UNSUPPORTED_WORKFLOW" || mode === "UNSUPPORTED_APPLIANCE_TYPE"

const getWorkflowForAiMessage = (event: AiMessageEvent): Workflow | null => {
  switch (event.content.workflowCategory) {
    case "DIAGNOSIS":
      return "diagnosis"
    case "ECOMMERCE":
      return "ecommerce"
    case "CUSTOMER_SERVICE":
      return "customer_service"
    case "UNSUPPORTED":
      return "unsupported"
    default:
      break
  }

  if (isUnsupportedMode(event.mode)) return "unsupported"
  if (event.mode === "CONTENT_DELIVERED") return "ecommerce"
  if (event.mode === "MAKE_DIAGNOSIS") return "diagnosis"
  if (event.causes.length > 0) return "diagnosis"
  return null
}

const hostCausesFromAiMessage = (
  event: AiMessageEvent,
): CaseCauseInfo[] =>
  event.causes.map((causeOnMessage) => ({
    name: causeOnMessage.cause.name,
    probability: causeOnMessage.probability,
  }))

const hostPartsFromAiMessage = (event: AiMessageEvent): CasePartInfo[] =>
  event.causes.flatMap((causeOnMessage) => {
    const part = causeOnMessage.part
    const partNumber = part?.manufacturerPartNumber
    if (!partNumber) return []
    return [
      {
        partNumber,
        name: part.name ?? null,
        partType: part.partType?.name ?? null,
      },
    ]
  })

const buildDiagnosisCaseInfo = (
  caseId: string,
  productInfo: ProductInfoSource | null | undefined,
  initIntake: unknown,
  event?: AiMessageEvent,
): CaseInfo => {
  const causes = event ? hostCausesFromAiMessage(event) : null
  const parts = event ? hostPartsFromAiMessage(event) : null
  const applianceAgeMonths =
    resolveAgeMonths(
      initIntake,
      (productInfo?.applianceAge as Parameters<
        typeof resolveAgeMonths
      >[1]) ?? null,
    ) ?? null

  return buildCaseInfo({
    caseId,
    product: toCaseInfoInput({
      productInfo: productInfo ?? {},
      causes: causes && causes.length > 0 ? causes : null,
      parts: parts && parts.length > 0 ? parts : null,
      applianceAgeMonths,
    }),
  })
}

export const useHostJourneyCaseTracking = (
  caseId: string,
  productInfo: ProductInfoSource | null | undefined,
  recommendation: Recommendation | null | undefined,
  events: CaseEvent[],
  initIntake?: unknown,
): void => {
  const trackedMessageIdsRef = useRef(new Set<string>())

  useEffect(() => {
    if (!usesJourneyHostEvents()) return
    syncHostIntakeFromProductInfo(caseId, productInfo ?? undefined, initIntake)
    setHostCaseInfo(buildDiagnosisCaseInfo(caseId, productInfo, initIntake))
  }, [caseId, productInfo, initIntake])

  useEffect(() => {
    if (!usesJourneyHostEvents()) return
    if (!recommendation) return

    emitHostRecommendationPreviewed(
      caseId,
      remediationTypeToHostJourneyStep(recommendation.type),
    )
  }, [caseId, recommendation])

  useEffect(() => {
    if (!usesJourneyHostEvents()) return

    for (const event of events) {
      if (!isTrackableAiMessage(event)) continue
      if (trackedMessageIdsRef.current.has(event.id)) continue
      trackedMessageIdsRef.current.add(event.id)

      const caseInfo = buildDiagnosisCaseInfo(
        caseId,
        productInfo,
        initIntake,
        event,
      )
      const workflow = getWorkflowForAiMessage(event)
      if (workflow === "unsupported") {
        setHostCaseInfo(caseInfo)
        emitHostDiagnosisInconclusive(caseId)
        continue
      }

      if (workflow === "diagnosis" && event.mode === "GRACEFUL_OFFRAMP") {
        continue
      }

      if (workflow === "diagnosis" && event.causes.length > 0) {
        setHostCaseInfo(caseInfo)
        emitHostDiagnosisCompleted(caseId)
        continue
      }

      if (
        workflow === "diagnosis" &&
        event.mode === "MAKE_DIAGNOSIS" &&
        event.causes.length === 0
      ) {
        setHostCaseInfo(caseInfo)
        emitHostDiagnosisInconclusive(caseId)
      }
    }
  }, [caseId, events, productInfo, initIntake])
}
